import {
    FC,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useCallback,
    useState
} from "react";
import { Link } from "react-router-dom";
import ProxyBoardIcon from "../../../assets/proxy_board_frame.svg";
import ProxyBoardIconWhite from "../../../assets/proxy_board_frame_white.svg";
import BoardIcon from "../../../assets/board_frame.svg";
import BoardIconWhite from "../../../assets/board_frame_white.svg";
import "./NavigationPanel.css";
import ArrowButton from "../../buttons/ArrowButton/ArrowButton";
import { AppContext } from "../../../App";
import Tooltip from "../../UiLib/CustomTooltip/CustomTooltip";
import { ISideBarBoard } from "../../../common/api_interfaces/common/ISideBar";

interface IBoardComponentProps {
    board: ISideBarBoard;
    showProxyBoards: boolean;
}

const BoardComponent: FC<IBoardComponentProps> = ({
    board,
    showProxyBoards,
}) => {
    const { toggleBoard, navBarInfo, navPanelHighlight } = useContext(AppContext);
    const [showTooltip, setShowTooltip] = useState<boolean>(false);

    const highlighted = useMemo(() => {
        if (navPanelHighlight) return false;
        return navBarInfo.boardId === board.id;
    }, [navBarInfo.boardId, board.id, navPanelHighlight]);

    const isOpen = useMemo(() => 
        navBarInfo.openBoardIds?.includes(board.id),
        [navBarInfo.openBoardIds, board.id]
    );

    const hasProxyBoards = useMemo(() => 
        board.boards && board.boards.length > 0 && showProxyBoards,
        [board.boards, showProxyBoards]
    );

    const handleClick = useCallback(() => {
        if (highlighted) {
            toggleBoard(board.id);
        }
    }, [highlighted, toggleBoard, board.id]);

    const handleArrowClick = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        toggleBoard(board.id);
    }, [toggleBoard, board.id]);

    useEffect(() => {
        const container = document.getElementById(`nav-panel-board-${board.id}`);
        const name = document.getElementById(`nav-panel-board-name-${board.id}`);

        if (container && name && name.offsetWidth > container.clientWidth) {
            setShowTooltip(true);
        }
    }, [board.id, board.name]);

    useLayoutEffect(() => {
        const container = document.getElementById(`nav-panel-board-${board.id}`);
        const name = document.getElementById(`nav-panel-board-name-${board.id}`);

        if (container && name && name.offsetWidth > container.clientWidth) {
            setShowTooltip(true);
        }
    }, [board.id, board.name]);

    const getBoardIcon = useMemo(() => {
        if (highlighted) {
            return board.isProxyBoard ? ProxyBoardIconWhite : BoardIconWhite;
        }
        return board.isProxyBoard ? ProxyBoardIcon : BoardIcon;
    }, [highlighted, board.isProxyBoard]);

    const arrowDirection = useMemo(() => 
        isOpen ? "top" : "bottom",
        [isOpen]
    );

    const proxyBoardsContent = useMemo(() => {
        if (!isOpen || !board.boards?.length) return null;

        return (
            <div>
                <div className="nav-panel_divider">ПРОКСИ-ДОСКИ</div>
                {board.boards.map((proxyBoard) => (
                    <BoardComponent
                        key={proxyBoard.id}
                        board={proxyBoard}
                        showProxyBoards={showProxyBoards}
                    />
                ))}
            </div>
        );
    }, [isOpen, board.boards, showProxyBoards]);

    if (!showProxyBoards && board.isProxyBoard) return null;

    return (
        <div>
            <Tooltip
                text={board.name ?? ""}
                offset={5}
                style={{
                    width: "fit-content",
                    maxWidth: "250px",
                    wordBreak: "break-all",
                    visibility: showTooltip ? "visible" : "hidden",
                }}
                placement={"right"}
            >
                <Link
                    className={`nav-panel_board ${
                        highlighted ? "highlighted" : ""
                    } ${board.isProxyBoard ? "nav-panel_proxy-board" : ""}`}
                    to={"/board/" + board.id}
                    onClick={handleClick}
                >
                    <div 
                        className="nav-panel_board-name"
                    >
                        <img
                            src={getBoardIcon}
                            className="board-icon"
                            alt="board-icon"
                        />
                        <div
                            id={`nav-panel-board-${board.id}`}
                            className="nav-panel_label"
                        >
                            <span id={`nav-panel-board-name-${board.id}`}>
                                {board.name}
                            </span>
                        </div>
                    </div>
                    {hasProxyBoards && (
                        <ArrowButton
                            onClick={handleArrowClick}
                            direction={arrowDirection}
                            whiteArrow={highlighted}
                        />
                    )}
                </Link>
            </Tooltip>
            {proxyBoardsContent}
        </div>
    );
};

export default BoardComponent;